/**
 * Safe structured-rich-text renderer for note and reply bodies.
 *
 * It renders only the node and mark allow-list from the notes contract and
 * never passes API content to raw HTML.
 */

import type { ReactNode } from "react";
import type { RichTextDocument, RichTextSpan } from "../../../types/notes";

interface RichTextContentProps {
  document: RichTextDocument;
  className?: string;
}

/**
 * Wrap a text span in its explicitly supported formatting marks.
 *
 * @param span - Structured text span
 * @param key - Stable React key
 * @returns Safely rendered span
 */
function renderSpan(span: RichTextSpan, key: string): ReactNode {
  let content: ReactNode = span.text || "\u00a0";
  if (span.marks?.includes("bold")) content = <strong>{content}</strong>;
  if (span.marks?.includes("italic")) content = <em>{content}</em>;
  if (span.marks?.includes("underline")) content = <u>{content}</u>;
  return <span key={key}>{content}</span>;
}

/**
 * Render a versioned note body using a constrained element allow-list.
 *
 * @param props - Structured document and optional wrapper classes
 * @returns Safe React rich-text content
 */
export function RichTextContent({
  document,
  className = "",
}: RichTextContentProps) {
  return (
    <div className={className}>
      {document.content.blocks.map((block, blockIndex) => {
        const content = block.content.map((span, spanIndex) =>
          renderSpan(span, `${blockIndex}-${spanIndex}`),
        );
        if (block.type === "bullet_list_item") {
          return <li key={blockIndex} className="ml-5 list-disc">{content}</li>;
        }
        if (block.type === "ordered_list_item") {
          return <li key={blockIndex} className="ml-5 list-decimal">{content}</li>;
        }
        return <p key={blockIndex}>{content}</p>;
      })}
    </div>
  );
}
